/**
 * useGisLayers
 *
 * Generates DeckGL layers for GIS asset visualization.
 * - Nodes → ScatterplotLayer (dots, colour by asset type, visible at zoom >= 14)
 * - Lines → PathLayer (lines, width by SIZE_PERC ratio, colour by asset type)
 *
 * Layer order: lines rendered first (below), nodes on top.
 * The travel layer (TripsLayer) is NOT included here — it is composed
 * above these layers in MainMap.tsx.
 */

import { useMemo } from 'react';
import { ScatterplotLayer, PathLayer } from 'deck.gl';
import type { PickingInfo } from 'deck.gl';
import type { GisNode, GisLine } from '@/utils/gisUtils';

// ── Colour palettes ─────────────────────────────────────────────────────────

const NODE_COLORS: Record<string, [number, number, number, number]> = {
	junctions: [130, 100, 220, 230],
	junction:  [130, 100, 220, 230],
	outfalls:  [180, 80,  200, 230],
	outfall:   [180, 80,  200, 230],
	storage:   [100, 150, 220, 230],
	default:   [120, 120, 200, 230],
};

const LINE_COLORS: Record<string, [number, number, number, number]> = {
	conduits: [0,   210, 255, 255],
	conduit:  [0,   210, 255, 255],
	weirs:    [200,  40, 255, 255],
	weir:     [200,  40, 255, 255],
	orifices: [255, 140,  20, 255],
	orifice:  [255, 140,  20, 255],
	pumps:    [255, 210,  30, 255],
	pump:     [255, 210,  30, 255],
	default:  [0,   210, 255, 255],
};

const MIN_LINE_WIDTH = 1;
const SIZE_PERC_MAX_WIDTH = 8; // max pixel width when SIZE_PERC = 1.0
const NODE_ZOOM_THRESHOLD = 14;

// ── Colour helpers ───────────────────────────────────────────────────────────

function getNodeColor(type: string): [number, number, number, number] {
	return NODE_COLORS[type?.toLowerCase()] ?? NODE_COLORS.default;
}

function getLineColor(type: string): [number, number, number, number] {
	return LINE_COLORS[type?.toLowerCase()] ?? LINE_COLORS.default;
}

function getLineWidth(sizePerc: number): number {
	if (sizePerc <= 0) return MIN_LINE_WIDTH;
	return MIN_LINE_WIDTH + Math.sqrt(sizePerc) * (SIZE_PERC_MAX_WIDTH - MIN_LINE_WIDTH);
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface UseGisLayersProps {
	nodes: GisNode[];
	lines: GisLine[];
	zoom: number;
	enabled: boolean;
	visible?: boolean;
}

export interface UseGisLayersResult {
	layers: any[];
}

export function useGisLayers({
	nodes,
	lines,
	zoom,
	enabled,
	visible = true,
}: UseGisLayersProps): UseGisLayersResult {

	const nodesLayer = useMemo(() => {
		if (!enabled || nodes.length === 0 || zoom < NODE_ZOOM_THRESHOLD) return null;

		const radius = zoom > 19 ? 5 : zoom > 18 ? 4 : zoom > 16 ? 3 : zoom > 14 ? 2 : 1.5;

		return new ScatterplotLayer({
			id: 'gis-nodes-layer',
			visible,
			data: nodes,
			pickable: true,
			filled: true,
			radiusMinPixels: radius,
			radiusMaxPixels: radius * 2,
			getPosition: (d: GisNode) => [d.longitude, d.latitude],
			getRadius: radius,
			getFillColor: (d: GisNode) => getNodeColor(d.type),
			getLineColor: [255, 255, 255, 180],
			lineWidthMinPixels: 0.5,
			autoHighlight: true,
			highlightColor: [255, 230, 50, 150],
			updateTriggers: {
				getRadius: [zoom],
			},
		});
	}, [enabled, nodes, zoom, visible]);

	const linesLayer = useMemo(() => {
		if (!enabled || lines.length === 0) return null;

		const validLines = lines.filter(l => Array.isArray(l.path) && l.path.length >= 2);
		if (validLines.length === 0) return null;

		return new PathLayer({
			id: 'gis-lines-layer',
			visible,
			data: validLines,
			pickable: true,
			widthUnits: 'pixels',
			widthMinPixels: 0.5,
			widthMaxPixels: 8,
			getPath: (d: GisLine) => d.path,
			getColor: (d: GisLine) => getLineColor(d.type),
			getWidth: (d: GisLine) => getLineWidth(d.sizePerc),
			autoHighlight: true,
			highlightColor: [255, 230, 50, 150],
		});
	}, [enabled, lines, visible]);

	const layers = useMemo(() => {
		const result: any[] = [];
		if (linesLayer) result.push(linesLayer);
		if (nodesLayer) result.push(nodesLayer);
		return result;
	}, [linesLayer, nodesLayer]);

	return { layers: layers };
}

const GIS_TOOLTIP_ZOOM_THRESHOLD = 14;

export function getGisTooltip({ object, layer }: PickingInfo, zoom: number) {
	if (!object) return null;
	if (layer?.id === 'gis-nodes-layer' || layer?.id === 'gis-lines-layer') {
		if (zoom < GIS_TOOLTIP_ZOOM_THRESHOLD) return null;
		const asset = object as GisNode | GisLine;
		return {
			html: `<div style="background:#232B36;color:white;padding:8px;border-radius:10px;font-size:12px;pointer-events:none;"><span><strong>Name:</strong> ${asset.id ?? 'N/A'}</span><br/><span><strong>Type:</strong> ${asset.type ?? 'N/A'}</span></div>`,
			style: { backgroundColor: 'transparent', fontSize: '12px', zIndex: '99', pointerEvents: 'none' },
		};
	}
	return null;
}

export default useGisLayers;
